import { chunk, compact, groupBy, uniq } from 'lodash'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import moment from 'moment'
import {
  SMS_OPS_AI_TURNS_QUERY,
  SMS_OPS_MESSAGES_QUERY,
  SMS_OPS_OPT_OUTS_QUERY,
  SMS_OPS_REPLY_ATTEMPTS_QUERY,
  SMS_OPS_TAKEOVER_EVENTS_QUERY,
  SMS_OPS_THREADS_QUERY,
  type SmsOpsAttemptRow,
  type SmsOpsEventRow,
  type SmsOpsMessageRow,
  type SmsOpsOptOutRow,
  type SmsOpsThreadRow,
  type SmsOpsTurnRow,
} from 'app/components/ai-agent-sms-ops/graphql'
import {
  type ReplyWait,
  type WaitStatus,
  WAIT_STATUSES,
  lateReplies,
  waitingOnAi,
} from 'app/components/ai-agent-sms-ops/smsReplyWaits'
import { buildMockSmsOps, type MockSmsOps } from 'app/components/ai-agent-sms-ops/smsAgentOpsMock'
import { FAILED_REPLY_STATUSES, failedReplies } from 'app/components/ai-agent-sms-ops/smsFailedReplies'
import { FAILURE_GROUPS, failureGroupFor } from 'app/components/ai-agent-sms-ops/smsFailures'
import {
  SILENT_THRESHOLD_HOURS,
  type WorseningMetric,
  totalCounts,
  worseningLocations,
} from 'app/components/ai-agent-sms-ops/smsAgentOpsMetrics'
import { TAKEOVER_CAUSES, takeoverCauseFor, takeoverLeaderboard } from 'app/components/ai-agent-sms-ops/smsTakeovers'
import type {
  FailedReplyFilter,
  FailureGroup,
  FailureGroupFilter,
  LiveList,
  SilentScope,
  SmsAgentOpsLocationRow,
  SmsAiWorkflowRow,
  SmsOpsFailedMessage,
  SmsOpsFailedReply,
  SmsOpsOptOut,
  SmsOpsReplyWait,
  SmsOpsTakeover,
  SmsOpsToolErrorTurn,
  TakeoverCause,
  TakeoverCauseFilter,
  WaitFilter,
} from 'app/types/sms-agent-ops'
import { slippingRows as buildSlippingRows } from 'app/components/ai-agent-voice-ops/voiceAgentOpsMetrics'
import { SortDirection } from 'app/generated/graphql'
import { gqlFetchList, gqlFetchOne, gqlQuery } from 'app/lib/graphqlClient'
import type { ContactJson, LocationJson, UserJson, WorkspaceJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

const LOCATION_FIELDS = `id name workspaceId products primarySmsNumber`

/**
 * One page per list. The lists are real queries, but there is no aggregate behind them
 * yet, so a range with more rows than this shows a partial list (plan D16).
 */
const LIST_PAGE_LIMIT = 1000

/** Thread ids per request for Waiting on a Reply; list queries go out as GET, so the id list must fit a URL. */
const THREAD_BATCH_SIZE = 100

/**
 * What may go into a Lucene search term from the URL (`?locationIds`) or the session: ids
 * only. The server ANDs its own tenant terms, so a crafted value can't widen results, but it
 * could break or skew the query.
 */
const SEARCH_ID_PATTERN = /^[A-Za-z0-9_-]+$/

/** An Event written by a person has source `User:<id>`; `System`, `Workflow:<id>` and the rest name no one. */
const USER_SOURCE_PREFIX = `User:`

/**
 * The acting user id from an Event source, or undefined when no person acted. Source is
 * colon-segmented `Type:id[:cause]` (TRK-6032 adds the optional cause tail), so take the id
 * segment rather than slicing the prefix, which would fold a cause tail into the id.
 */
function userIdFromSource(source: string): string | undefined {
  if (!source.startsWith(USER_SOURCE_PREFIX)) return undefined
  return source.split(`:`)[1] || undefined
}

/** A pending text younger than this may still send; older ones count as stuck (plan D12). */
const STUCK_PENDING_AFTER = `now-1h`

/**
 * State for the super-user Ops tab on the SMS Agent page: the scope it looks at, the
 * locations and event lists it loaded, the list filters, and every table, chart and
 * card derived from them.
 *
 * The counts (cards, charts, Location Breakdown, takeovers by location and staff,
 * workflows, Getting Worse, Silent Agents) are simulated by `buildMockSmsOps` until
 * the ops resolvers exist (docs/plans/2026-09-25-sms-agent-ops-analytics.md). The
 * four event lists (takeovers, failed texts, opt-outs, AI tool errors) are real queries.
 */
export class SmsAgentOpsStore {
  /** Scope, set from the page's `?fromDate` / `?toDate` / `?locationIds`. */
  fromDate = moment().startOf(`month`).format(`YYYY-MM-DD`)
  toDate = moment().endOf(`month`).format(`YYYY-MM-DD`)
  locationIds: string[] = []
  /** The session's org. The server already scopes every list to it; the term only keeps the searches explicit. */
  orgId: string | undefined = undefined

  locations: LocationJson[] | undefined = undefined
  workspaceNames: Record<string, string> = {}
  takeoverEvents: SmsOpsEventRow[] | undefined = undefined
  userNames: Record<string, string> = {}
  failedMessages: SmsOpsMessageRow[] | undefined = undefined
  optOutRows: SmsOpsOptOutRow[] | undefined = undefined
  aiTurns: SmsOpsTurnRow[] | undefined = undefined
  /** The AI agent's texts in range, for the per-workflow table on the Agents SMS dashboard. */
  aiTexts: SmsOpsMessageRow[] | undefined = undefined
  /** Replied Late inputs: patient texts in range, their threads, and the AI texts on those threads. */
  patientTexts: SmsOpsMessageRow[] | undefined = undefined
  replyThreads: SmsOpsThreadRow[] = []
  agentTexts: SmsOpsMessageRow[] = []
  /** AI-on threads, and the contact behind each waiting one. */
  waitingThreads: SmsOpsThreadRow[] | undefined = undefined
  waitingContactIds: Record<string, string> = {}
  /** Failed AI reply attempts in range (TRK-5908), and the successful attempts after them on the same threads. */
  failedAttempts: SmsOpsAttemptRow[] | undefined = undefined
  laterAttempts: SmsOpsAttemptRow[] = []
  /** Failed attempts in the hour before the waiting check, for the banner's reason. Not held to the date range. */
  recentFailedAttempts: SmsOpsAttemptRow[] = []
  /** Names of the contacts in the Waiting on a Reply and Failed AI Replies lists, by contact id. */
  contactNames: Record<string, string> = {}
  waitingCheckedAt: Date | undefined = undefined
  /** A live list whose query failed, with the error, so it reads as broken rather than empty. */
  listErrors: Partial<Record<LiveList, string>> = {}

  takeoverCauseFilter: TakeoverCauseFilter = `all`
  failureFilter: FailureGroupFilter = `all`
  worseningMetric: WorseningMetric = `takeoverRate`
  silentScope: SilentScope = `silent`
  waitFilter: WaitFilter = `all`
  failedReplyFilter: FailedReplyFilter = `all`

  /** Bumped per request; a response from an older scope must not overwrite a newer one. */
  private locationsVersion = 0
  private takeoversVersion = 0
  private failuresVersion = 0
  private optOutsVersion = 0
  private turnsVersion = 0
  private repliesVersion = 0
  private waitingVersion = 0
  private failedRepliesVersion = 0
  private aiTextsVersion = 0

  constructor() {
    // makeObservable (not makeAutoObservable): excludes private version counters
    // from observability and uses observable.ref for large payloads, neither of
    // which makeAutoObservable can express.
    makeObservable<
      this,
      | `locationsVersion`
      | `takeoversVersion`
      | `failuresVersion`
      | `optOutsVersion`
      | `turnsVersion`
      | `repliesVersion`
      | `waitingVersion`
      | `failedRepliesVersion`
      | `aiTextsVersion`
    >(this, {
      fromDate: observable,
      toDate: observable,
      locationIds: observable,
      orgId: observable,
      locations: observable.ref,
      workspaceNames: observable.ref,
      takeoverEvents: observable.ref,
      listErrors: observable.ref,
      userNames: observable.ref,
      failedMessages: observable.ref,
      optOutRows: observable.ref,
      aiTurns: observable.ref,
      aiTexts: observable.ref,
      patientTexts: observable.ref,
      replyThreads: observable.ref,
      agentTexts: observable.ref,
      waitingThreads: observable.ref,
      waitingContactIds: observable.ref,
      failedAttempts: observable.ref,
      laterAttempts: observable.ref,
      recentFailedAttempts: observable.ref,
      contactNames: observable.ref,
      waitingCheckedAt: observable.ref,
      takeoverCauseFilter: observable,
      failureFilter: observable,
      worseningMetric: observable,
      silentScope: observable,
      waitFilter: observable,
      failedReplyFilter: observable,
      locationsVersion: false,
      takeoversVersion: false,
      failuresVersion: false,
      optOutsVersion: false,
      turnsVersion: false,
      repliesVersion: false,
      waitingVersion: false,
      failedRepliesVersion: false,
      aiTextsVersion: false,
      previousFromDate: computed,
      previousToDate: computed,
      isLoading: computed,
      scopedLocations: computed,
      locationNames: computed,
      optOuts: computed,
      metrics: computed,
      daily: computed,
      locationRows: computed,
      summary: computed,
      leaderboardRows: computed,
      staffRows: computed,
      workflowRows: computed,
      worseningRows: computed,
      allSilentRows: computed,
      silentRows: computed,
      takeovers: computed,
      takeoverCounts: computed,
      visibleTakeovers: computed,
      failures: computed,
      failureCounts: computed,
      visibleFailures: computed,
      toolErrorTurns: computed,
      replyWaits: computed,
      waitCounts: computed,
      visibleReplyWaits: computed,
      lastAiReplyDate: computed,
      failedReplyRows: computed,
      failedReplyCounts: computed,
      visibleFailedReplies: computed,
      recentUnansweredFailures: computed,
      aiWorkflowRows: computed,
      setScope: action.bound,
      setTakeoverCauseFilter: action.bound,
      setFailureFilter: action.bound,
      setWorseningMetric: action.bound,
      setSilentScope: action.bound,
      setWaitFilter: action.bound,
      setFailedReplyFilter: action.bound,
      loadLocations: action.bound,
      loadTakeovers: action.bound,
      loadFailures: action.bound,
      loadOptOuts: action.bound,
      loadAiTurns: action.bound,
      loadLateReplies: action.bound,
      loadWaiting: action.bound,
      loadFailedReplies: action.bound,
      loadAiTexts: action.bound,
      reset: action.bound,
    })
  }

  /**
   * The dates and ids come from the URL, and all of them end up inside search strings, so
   * only strict `YYYY-MM-DD` dates and plain ids get through; anything else falls back to
   * this month or is dropped.
   */
  setScope(fromDate: string, toDate: string, locationIds: string[], orgId?: string) {
    this.fromDate = moment(fromDate, `YYYY-MM-DD`, true).isValid()
      ? fromDate
      : moment().startOf(`month`).format(`YYYY-MM-DD`)
    this.toDate = moment(toDate, `YYYY-MM-DD`, true).isValid() ? toDate : moment().endOf(`month`).format(`YYYY-MM-DD`)
    this.locationIds = locationIds.filter((id) => SEARCH_ID_PATTERN.test(id))
    this.orgId = orgId && SEARCH_ID_PATTERN.test(orgId) ? orgId : undefined
  }

  setTakeoverCauseFilter(filter: TakeoverCauseFilter) {
    this.takeoverCauseFilter = filter
  }

  setFailureFilter(filter: FailureGroupFilter) {
    this.failureFilter = filter
  }

  setWorseningMetric(metric: WorseningMetric) {
    this.worseningMetric = metric
  }

  setWaitFilter(filter: WaitFilter) {
    this.waitFilter = filter
  }

  setFailedReplyFilter(filter: FailedReplyFilter) {
    this.failedReplyFilter = filter
  }

  setSilentScope(scope: SilentScope) {
    this.silentScope = scope
  }

  /** The search terms every list shares: the session's org, the range on `dateField`, and the location filter. */
  private listSearch(dateField: string, ...terms: string[]): string {
    return compact([
      this.orgId ? `orgId:"${this.orgId}"` : undefined,
      `${dateField}:[${this.fromDate} TO ${this.toDate}]`,
      this.locationIds.length ? `locationId:(${this.locationIds.join(` OR `)})` : undefined,
      ...terms,
    ]).join(` `)
  }

  /** Every location in the session's scope: any location can text (plan D3). */
  loadLocations() {
    const version = ++this.locationsVersion
    bg(async () => {
      const [locationList, workspaceList] = await Promise.all([
        gqlFetchList<LocationJson>(`locations`, { pagination: { limit: 1000 } }, LOCATION_FIELDS),
        gqlFetchList<WorkspaceJson>(`workspaces`, { pagination: { limit: 1000 } }, `id name`),
      ])
      if (version !== this.locationsVersion) return
      runInAction(() => {
        this.locations = locationList.hits
        this.workspaceNames = Object.fromEntries(workspaceList.hits.map((ws) => [ws.id!, ws.name ?? ``]))
      })
    })
  }

  /**
   * Thread change Events that turned `aiEnabled` off (plan D8), and the names of the
   * staff behind them. These Events only exist from 2026-09-01, so an earlier range
   * shows an empty list.
   */
  loadTakeovers() {
    const version = ++this.takeoversVersion
    this.takeoverEvents = undefined
    bg(async () => {
      const { data, error } = await gqlQuery(SMS_OPS_TAKEOVER_EVENTS_QUERY, {
        search: this.listSearch(`eventDate`, `type:Thread`, `action:updated`, `data.aiEnabled.to:false`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `eventDate`, direction: SortDirection.Desc },
      })
      const events = data?.events.hits ?? []
      const userIds = uniq(
        events
          .map((event) => userIdFromSource(event.source))
          .filter((id): id is string => Boolean(id))
          // They go into the users search below, so plain ids only.
          .filter((id) => SEARCH_ID_PATTERN.test(id)),
      )
      // One `users` list request covers everyone in the session's tenant. That list can't see
      // super users and support staff whose home is another workspace, and they take over often,
      // so only the ids it misses fall back to `user(id)`. TRK-5868 returns names server-side.
      const listed = userIds.length
        ? (
            await gqlFetchList<UserJson>(
              `users`,
              { search: `id:(${userIds.join(` OR `)})`, pagination: { limit: userIds.length } },
              `id name`,
            )
          ).hits
        : []
      const listedIds = new Set(listed.map((user) => user.id))
      const unlisted = await Promise.all(
        userIds.filter((id) => !listedIds.has(id)).map((id) => gqlFetchOne<UserJson>(`user`, id, `id name`)),
      )
      const users = [...listed, ...unlisted]
      if (version !== this.takeoversVersion) return
      if (error) logError(`SMS Ops takeovers query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, takeovers: error?.message }
        this.takeoverEvents = events
        this.userNames = Object.fromEntries(compact(users).map((user) => [user.id!, user.name ?? ``]))
      })
    })
  }

  /** Failed SMS: carrier failures and policy blocks in range, plus sends stuck pending (plan D12). */
  loadFailures() {
    const version = ++this.failuresVersion
    this.failedMessages = undefined
    bg(async () => {
      const { data, error } = await gqlQuery(SMS_OPS_MESSAGES_QUERY, {
        search: this.listSearch(
          `createdDate`,
          `NOT channel:email`,
          `(status:(failed OR undelivered) OR (status:pending AND createdDate:[* TO ${STUCK_PENDING_AFTER}]))`,
        ),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      if (version !== this.failuresVersion) return
      if (error) logError(`SMS Ops failures query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, failures: error?.message }
        this.failedMessages = data?.messages.hits ?? []
      })
    })
  }

  /** Opt-outs created in range. START deletes the record, so a number that opted back in is gone. */
  loadOptOuts() {
    const version = ++this.optOutsVersion
    this.optOutRows = undefined
    bg(async () => {
      const { data, error } = await gqlQuery(SMS_OPS_OPT_OUTS_QUERY, {
        search: this.listSearch(`createdDate`, `type:PHONE`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      if (version !== this.optOutsVersion) return
      if (error) logError(`SMS Ops optOuts query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, optOuts: error?.message }
        this.optOutRows = data?.optOuts.hits ?? []
      })
    })
  }

  /** AI replies in range; the Tool Errors list keeps the ones with a failed tool result. */
  loadAiTurns() {
    const version = ++this.turnsVersion
    this.aiTurns = undefined
    bg(async () => {
      const { data, error } = await gqlQuery(SMS_OPS_AI_TURNS_QUERY, {
        search: this.listSearch(`createdDate`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      if (version !== this.turnsVersion) return
      if (error) logError(`SMS Ops aiTurns query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, aiTurns: error?.message }
        this.aiTurns = data?.aiConversationTurns.hits ?? []
      })
    })
  }

  /** The AI agent's texts in range, with the workflow each one ran under. */
  loadAiTexts() {
    const version = ++this.aiTextsVersion
    this.aiTexts = undefined
    bg(async () => {
      const { data, error } = await gqlQuery(SMS_OPS_MESSAGES_QUERY, {
        search: this.listSearch(`createdDate`, `isAgent:true`, `direction:outbound`, `NOT channel:email`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      if (version !== this.aiTextsVersion) return
      if (error) logError(`SMS Ops aiTexts query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, aiTexts: error?.message }
        this.aiTexts = data?.messages.hits ?? []
      })
    })
  }

  /** The Waiting half of Waiting on a Reply: AI-on threads, then the contacts on the waiting ones. */
  loadWaiting() {
    const version = ++this.waitingVersion
    this.waitingThreads = undefined
    bg(async () => {
      const orgTerm = this.orgId ? `orgId:"${this.orgId}" ` : ``
      const locationTerm = this.locationIds.length ? ` locationId:(${this.locationIds.join(` OR `)})` : ``
      const threads = await gqlQuery(SMS_OPS_THREADS_QUERY, {
        search: `${orgTerm}aiEnabled:true${locationTerm}`,
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `lastIncomingMessageDate`, direction: SortDirection.Desc },
      })
      const waitingThreads = threads.data?.threads.hits ?? []
      const checkedAt = new Date()
      const threadIds = waitingOnAi(waitingThreads, checkedAt)
        .map((row) => row.threadId)
        .filter((id) => SEARCH_ID_PATTERN.test(id))
      const batches = await Promise.all(
        chunk(threadIds, THREAD_BATCH_SIZE).map((ids) =>
          gqlQuery(SMS_OPS_MESSAGES_QUERY, {
            search: `${orgTerm}threadId:(${ids.join(` OR `)}) direction:inbound _exists_:contactId`,
            pagination: { limit: LIST_PAGE_LIMIT },
            sort: { field: `createdDate`, direction: SortDirection.Desc },
          }),
        ),
      )
      const waitingContactIds: Record<string, string> = {}
      for (const text of batches.flatMap((batch) => batch.data?.messages.hits ?? [])) {
        if (text.threadId && text.contactId) waitingContactIds[text.threadId] ??= text.contactId
      }
      const hourAgo = new Date(checkedAt.getTime() - 60 * 60_000).toISOString()
      const [names, recent] = await Promise.all([
        this.fetchContactNames(waitingOnAi(waitingThreads, checkedAt, waitingContactIds)),
        gqlQuery(SMS_OPS_REPLY_ATTEMPTS_QUERY, {
          search: `${orgTerm}failed:true createdDate:[${hourAgo} TO *]${locationTerm}`,
          pagination: { limit: LIST_PAGE_LIMIT },
          sort: { field: `createdDate`, direction: SortDirection.Desc },
        }),
      ])
      if (version !== this.waitingVersion) return
      const error = threads.error ?? batches.map((batch) => batch.error).find(Boolean) ?? recent.error ?? undefined
      if (error) logError(`SMS Ops waiting query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, waiting: error?.message }
        this.waitingThreads = waitingThreads
        this.waitingContactIds = waitingContactIds
        this.contactNames = { ...this.contactNames, ...names }
        this.recentFailedAttempts = recent.data?.aiConversationTurns.hits ?? []
        this.waitingCheckedAt = checkedAt
      })
    })
  }

  /**
   * The Replied Late half (plan D37): the patient texts in range, then, for their threads, the
   * thread state and every AI text. No aggregate exists yet, so this reads one page of patient
   * texts and fetches their threads in batches small enough for a GET query string.
   */
  loadLateReplies() {
    const version = ++this.repliesVersion
    this.patientTexts = undefined
    bg(async () => {
      const texts = await gqlQuery(SMS_OPS_MESSAGES_QUERY, {
        search: this.listSearch(`createdDate`, `direction:inbound`, `NOT channel:email`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      const patientTexts = texts.data?.messages.hits ?? []
      const threadIds = uniq(
        compact(patientTexts.map((text) => text.threadId)).filter((id) => SEARCH_ID_PATTERN.test(id)),
      )
      const orgTerm = this.orgId ? `orgId:"${this.orgId}" ` : ``
      const batches = await Promise.all(
        chunk(threadIds, THREAD_BATCH_SIZE).map(async (ids) => {
          const idList = ids.join(` OR `)
          const [threads, agent] = await Promise.all([
            gqlQuery(SMS_OPS_THREADS_QUERY, { search: `${orgTerm}id:(${idList})`, pagination: { limit: ids.length } }),
            gqlQuery(SMS_OPS_MESSAGES_QUERY, {
              search: `${orgTerm}threadId:(${idList}) isAgent:true`,
              pagination: { limit: LIST_PAGE_LIMIT },
              sort: { field: `createdDate`, direction: SortDirection.Asc },
            }),
          ])
          return { threads, agent }
        }),
      )
      const replyThreads = batches.flatMap((batch) => batch.threads.data?.threads.hits ?? [])
      const agentTexts = batches.flatMap((batch) => batch.agent.data?.messages.hits ?? [])
      const names = await this.fetchContactNames(lateReplies(patientTexts, agentTexts, replyThreads))
      if (version !== this.repliesVersion) return
      const error =
        texts.error ?? batches.map((batch) => batch.threads.error ?? batch.agent.error).find(Boolean) ?? undefined
      if (error) logError(`SMS Ops replies query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, replies: error?.message }
        this.patientTexts = patientTexts
        this.replyThreads = replyThreads
        this.agentTexts = agentTexts
        this.contactNames = { ...this.contactNames, ...names }
      })
    })
  }

  /**
   * Failed AI Replies (plan D39): the failed attempts in range, then every successful attempt on
   * their threads since the earliest failure, which is what marks a text answered later. One page
   * of failures; batches of threads small enough for a GET query string.
   */
  loadFailedReplies() {
    const version = ++this.failedRepliesVersion
    this.failedAttempts = undefined
    bg(async () => {
      const failed = await gqlQuery(SMS_OPS_REPLY_ATTEMPTS_QUERY, {
        search: this.listSearch(`createdDate`, `failed:true`),
        pagination: { limit: LIST_PAGE_LIMIT },
        sort: { field: `createdDate`, direction: SortDirection.Desc },
      })
      const failedAttempts = failed.data?.aiConversationTurns.hits ?? []
      const threadIds = uniq(compact(failedAttempts.map((row) => row.threadId))).filter((id) =>
        SEARCH_ID_PATTERN.test(id),
      )
      const earliest = failedAttempts.map((row) => row.createdDate).sort()[0]
      const orgTerm = this.orgId ? `orgId:"${this.orgId}" ` : ``
      const [later, names] = await Promise.all([
        Promise.all(
          chunk(threadIds, THREAD_BATCH_SIZE).map((ids) =>
            gqlQuery(SMS_OPS_REPLY_ATTEMPTS_QUERY, {
              search: `${orgTerm}threadId:(${ids.join(` OR `)}) NOT failed:true createdDate:[${earliest} TO *]`,
              pagination: { limit: LIST_PAGE_LIMIT },
              sort: { field: `createdDate`, direction: SortDirection.Asc },
            }),
          ),
        ),
        this.fetchContactNames(failedAttempts),
      ])
      if (version !== this.failedRepliesVersion) return
      const error = failed.error ?? later.map((batch) => batch.error).find(Boolean) ?? undefined
      if (error) logError(`SMS Ops failed replies query failed`, error)
      runInAction(() => {
        this.listErrors = { ...this.listErrors, failedReplies: error?.message }
        this.failedAttempts = failedAttempts
        this.laterAttempts = later.flatMap((batch) => batch.data?.aiConversationTurns.hits ?? [])
        this.contactNames = { ...this.contactNames, ...names }
      })
    })
  }

  /** Names for the lists' contacts not already known. Threads rarely carry one; contacts do. */
  private async fetchContactNames(rows: { contactId?: string | null }[]): Promise<Record<string, string>> {
    const ids = uniq(compact(rows.map((row) => row.contactId))).filter(
      (id) => SEARCH_ID_PATTERN.test(id) && !(id in this.contactNames),
    )
    const lists = await Promise.all(
      chunk(ids, THREAD_BATCH_SIZE).map((batch) =>
        gqlFetchList<ContactJson>(
          `contacts`,
          { search: `id:(${batch.join(` OR `)})`, pagination: { limit: batch.length } },
          `id name`,
        ),
      ),
    )
    return Object.fromEntries(lists.flatMap((list) => list.hits).map((contact) => [contact.id!, contact.name ?? ``]))
  }

  reset() {
    this.locations = undefined
    this.workspaceNames = {}
    this.takeoverEvents = undefined
    this.userNames = {}
    this.failedMessages = undefined
    this.optOutRows = undefined
    this.aiTurns = undefined
    this.aiTexts = undefined
    this.patientTexts = undefined
    this.replyThreads = []
    this.agentTexts = []
    this.waitingThreads = undefined
    this.waitingContactIds = {}
    this.failedAttempts = undefined
    this.laterAttempts = []
    this.recentFailedAttempts = []
    this.contactNames = {}
    this.waitingCheckedAt = undefined
    this.waitFilter = `all`
    this.failedReplyFilter = `all`
    this.listErrors = {}
    this.takeoverCauseFilter = `all`
    this.failureFilter = `all`
    this.worseningMetric = `takeoverRate`
    this.silentScope = `silent`
  }

  /** The comparison's previous period: the same number of days, ending the day before the range. */
  get previousFromDate(): string {
    const rangeDays = moment.utc(this.toDate).diff(moment.utc(this.fromDate), `days`) + 1
    return moment.utc(this.fromDate).subtract(rangeDays, `days`).format(`YYYY-MM-DD`)
  }

  get previousToDate(): string {
    return moment.utc(this.fromDate).subtract(1, `day`).format(`YYYY-MM-DD`)
  }

  get isLoading(): boolean {
    return !this.locationRows
  }

  get scopedLocations(): LocationJson[] | undefined {
    return this.locations?.filter((location) => !this.locationIds.length || this.locationIds.includes(location.id!))
  }

  /**
   * TEMPORARY: simulated metrics standing in for the ops resolvers. When they exist this
   * becomes an observable set by `loadMetrics` / `loadDailyMetrics`, as on the Voice Ops store.
   */
  get metrics(): MockSmsOps | undefined {
    if (!this.scopedLocations) return undefined
    return buildMockSmsOps(
      this.scopedLocations,
      this.fromDate,
      this.toDate,
      this.previousFromDate,
      this.previousToDate,
      moment(),
    )
  }

  get daily() {
    return this.metrics?.daily ?? []
  }

  /** One row per location that texted in range (plan D3). */
  get locationRows(): SmsAgentOpsLocationRow[] | undefined {
    if (!this.scopedLocations || !this.metrics) return undefined
    const locationById = new Map(this.scopedLocations.map((location) => [location.id!, location]))
    return this.metrics.rows.map(({ locationId, counts, lastAgentTextDate }) => {
      const location = locationById.get(locationId)
      return {
        ...counts,
        locationId,
        locationName: location?.name ?? ``,
        workspaceId: location?.workspaceId ?? undefined,
        workspaceName: location?.workspaceId ? this.workspaceNames[location.workspaceId] : undefined,
        hasSmsAgent: !!location?.products?.includes(`SMS Agent`),
        lastAgentTextDate,
      }
    })
  }

  get summary() {
    return totalCounts(this.locationRows ?? [])
  }

  /** SMS Agent locations, worst takeover rate first (plan D10). */
  get leaderboardRows(): SmsAgentOpsLocationRow[] | undefined {
    return this.locationRows && takeoverLeaderboard(this.locationRows.filter((row) => row.hasSmsAgent))
  }

  get staffRows() {
    return this.metrics?.staffRows
  }

  get workflowRows() {
    return this.metrics?.workflowRows
  }

  /** Locations whose chosen rate rose; takeover rate only compares SMS Agent locations. */
  get worseningRows(): SmsAgentOpsLocationRow[] | undefined {
    if (!this.locationRows) return undefined
    const rows =
      this.worseningMetric === `takeoverRate` ? this.locationRows.filter((row) => row.hasSmsAgent) : this.locationRows
    return worseningLocations(rows, this.worseningMetric)
  }

  get allSilentRows() {
    if (!this.scopedLocations || !this.metrics) return undefined
    return buildSlippingRows(
      this.scopedLocations.filter((location) => location.products?.includes(`SMS Agent`)),
      this.metrics.locationDaily,
      this.fromDate,
      this.toDate,
      SILENT_THRESHOLD_HOURS,
      moment(),
    )
  }

  get silentRows() {
    return this.silentScope === `all` ? this.allSilentRows : this.allSilentRows?.filter((row) => row.isSlipping)
  }

  /** Location id → name, for the live lists. */
  get locationNames(): Record<string, string> {
    return Object.fromEntries((this.locations ?? []).map((location) => [location.id!, location.name ?? ``]))
  }

  get takeovers(): SmsOpsTakeover[] | undefined {
    return this.takeoverEvents?.flatMap((event) => {
      const cause = takeoverCauseFor(event)
      if (!cause) return []
      const userId = userIdFromSource(event.source)
      return [
        {
          ...event,
          locationName: this.locationNames[event.locationId] ?? ``,
          cause,
          userId,
          userName: userId ? this.userNames[userId] : undefined,
        },
      ]
    })
  }

  get takeoverCounts(): Record<TakeoverCauseFilter, number> {
    return Object.fromEntries([
      [`all`, this.takeovers?.length ?? 0],
      ...TAKEOVER_CAUSES.map((cause) => [cause, this.takeovers?.filter((t) => t.cause === cause).length ?? 0]),
    ]) as Record<TakeoverCauseFilter, number>
  }

  get visibleTakeovers() {
    return this.takeoverCauseFilter === `all`
      ? this.takeovers
      : this.takeovers?.filter((takeover) => takeover.cause === this.takeoverCauseFilter)
  }

  /** Failed texts with their group. A pending text scheduled for later is waiting, not stuck. */
  get failures(): SmsOpsFailedMessage[] | undefined {
    const now = moment()
    return this.failedMessages?.flatMap((message) => {
      const failureGroup = failureGroupFor(message)
      if (!failureGroup) return []
      if (failureGroup === `stuckPending` && message.sendDate && moment(message.sendDate).isAfter(now)) return []
      return [{ ...message, locationName: this.locationNames[message.locationId] ?? ``, failureGroup }]
    })
  }

  get failureCounts(): Record<FailureGroupFilter, number> {
    return Object.fromEntries([
      [`all`, this.failures?.length ?? 0],
      ...FAILURE_GROUPS.map((group) => [group, this.failures?.filter((m) => m.failureGroup === group).length ?? 0]),
    ]) as Record<FailureGroupFilter, number>
  }

  get visibleFailures() {
    return this.failureFilter === `all`
      ? this.failures
      : this.failures?.filter((message) => message.failureGroup === this.failureFilter)
  }

  get optOuts(): SmsOpsOptOut[] | undefined {
    return this.optOutRows?.map((optOut) => ({ ...optOut, locationName: this.locationNames[optOut.locationId] ?? `` }))
  }

  /**
   * Waiting on a Reply (plan D37): everyone waiting on the AI now, longest first, then the
   * conversations the AI answered late in range, newest first. A conversation that is waiting
   * now shows once, as waiting.
   */
  get replyWaits(): SmsOpsReplyWait[] | undefined {
    if (!this.waitingThreads || !this.waitingCheckedAt || !this.patientTexts) return undefined
    const waiting = waitingOnAi(this.waitingThreads, this.waitingCheckedAt, this.waitingContactIds)
    const waitingThreadIds = new Set(waiting.map((row) => row.threadId))
    const late = lateReplies(this.patientTexts, this.agentTexts, this.replyThreads).filter(
      (row) => !waitingThreadIds.has(row.threadId),
    )
    return [...waiting, ...late].map((row) => ({
      ...row,
      locationName: this.locationNames[row.locationId] ?? ``,
      workspaceName: this.workspaceNames[row.workspaceId],
      contactName: (row.contactId && this.contactNames[row.contactId]) || row.contactName,
    }))
  }

  get waitCounts(): Record<WaitFilter, number> {
    return Object.fromEntries([
      [`all`, this.replyWaits?.length ?? 0],
      ...WAIT_STATUSES.map((status) => [status, this.replyWaits?.filter((row) => row.status === status).length ?? 0]),
    ]) as Record<WaitFilter, number>
  }

  get visibleReplyWaits() {
    return this.waitFilter === `all`
      ? this.replyWaits
      : this.replyWaits?.filter((row) => row.status === this.waitFilter)
  }

  /**
   * The AI's latest text on any AI thread in scope, so the banner can tell an outage (no reply
   * since the patients started waiting) from a few one-off misses.
   */
  get lastAiReplyDate(): string | undefined {
    const dates = compact(this.waitingThreads?.map((thread) => thread.agentLastMessageDate))
    return dates.length
      ? dates.reduce((latest, date) => (Date.parse(date) > Date.parse(latest) ? date : latest))
      : undefined
  }

  /** Failed AI Replies (plan D39): one row per patient text the AI failed to answer in range. */
  get failedReplyRows(): SmsOpsFailedReply[] | undefined {
    if (!this.failedAttempts) return undefined
    return failedReplies(this.failedAttempts, this.laterAttempts).map((row) => ({
      ...row,
      locationName: this.locationNames[row.locationId] ?? ``,
      workspaceName: this.workspaceNames[row.workspaceId],
      contactName: (row.contactId && this.contactNames[row.contactId]) || undefined,
    }))
  }

  get failedReplyCounts(): Record<FailedReplyFilter, number> {
    return Object.fromEntries([
      [`all`, this.failedReplyRows?.length ?? 0],
      ...FAILED_REPLY_STATUSES.map((status) => [
        status,
        this.failedReplyRows?.filter((row) => row.status === status).length ?? 0,
      ]),
    ]) as Record<FailedReplyFilter, number>
  }

  get visibleFailedReplies() {
    return this.failedReplyFilter === `all`
      ? this.failedReplyRows
      : this.failedReplyRows?.filter((row) => row.status === this.failedReplyFilter)
  }

  /**
   * The last hour's failed texts the AI has not answered since, newest first, for the banner's
   * reason. Answered means the AI texted on the thread after the failure; a thread the AI no
   * longer handles is not the AI's to answer, so it drops out.
   */
  get recentUnansweredFailures(): SmsOpsFailedReply[] {
    const aiThreads = new Map((this.waitingThreads ?? []).map((thread) => [thread.id, thread]))
    return failedReplies(this.recentFailedAttempts, [])
      .filter((row) => {
        const thread = aiThreads.get(row.threadId)
        if (!thread) return false
        return !thread.agentLastMessageDate || Date.parse(thread.agentLastMessageDate) < Date.parse(row.lastFailedAt)
      })
      .sort((a, b) => Date.parse(b.lastFailedAt) - Date.parse(a.lastFailedAt))
      .map((row) => ({ ...row, locationName: this.locationNames[row.locationId] ?? `` }))
  }

  /** Per workflow: the AI's texts, the threads they went to, and how many of those threads staff took over. */
  get aiWorkflowRows(): SmsAiWorkflowRow[] | undefined {
    if (!this.aiTexts) return undefined
    const takenOver = new Set(this.takeovers?.map((takeover) => takeover.typeId))
    const groups = groupBy(this.aiTexts, (text) => text.aiWorkflowName ?? `No workflow`)
    return Object.entries(groups).map(([workflowName, texts]) => {
      const threadIds = uniq(compact(texts.map((text) => text.threadId)))
      return {
        workflowName,
        aiTexts: texts.length,
        conversations: threadIds.length,
        takeovers: threadIds.filter((id) => takenOver.has(id)).length,
      }
    })
  }

  get toolErrorTurns(): SmsOpsToolErrorTurn[] | undefined {
    return this.aiTurns
      ?.filter((turn) => turn.steps?.some((step) => step.toolResults?.some((result) => result.isError)))
      .map((turn) => ({ ...turn, locationName: this.locationNames[turn.locationId] ?? `` }))
  }
}
